<template>
  <MainLayout>

    <!-- Header Moderno -->
    <PageHeader icon="ri-user-voice-line" title="Nueva Tutoría" :subtitle="headerSubtitle" button-text="Actualizar"
      button-icon="ri-loop-right-line" @action="actualizarDisponibilidad" />

    <div class="content-container" v-if="cursoActual">

      <!-- Card de Información -->
      <div class="info-card">
        <div class="info-section">
          <div class="info-row">
            <span class="info-label">
              <i class="ri-book-2-line"></i>
              Módulo:
            </span>
            <span class="info-value">{{ cursoActual.nivel ? cursoActual.nivel.descripcion : cursoActual.nombre }}</span>
          </div>

          <div class="info-row" v-if="sedes.length > 0 && sedeActual">
            <span class="info-label">
              <i class="ri-map-pin-line"></i>
              Sede:
            </span>
            <span class="info-value">{{ sedeActual.nombre }}</span>
            <button v-if="puedeCambiarSede && cambiandoTema == false" @click="cambiandoTema = true" class="btn-change">
              Cambiar
            </button>
          </div>

          <div class="info-row" v-if="siguienteTema && siguienteTema.tipoObj">
            <span class="info-label">
              <i class="ri-door-open-line"></i>
              Salón:
            </span>
            <span class="info-value">{{ siguienteTema.tipoObj.nombre_largo }}</span>
          </div>
        </div>

        <!-- Sin horarios habilitados: se evalúa primero y reemplaza los selectores -->
        <div v-if="sinHorariosHabilitados" class="no-availability" role="status">
          <i class="ri-information-line"></i>
          <span>Por el momento no hay horarios habilitados para programar tutorías. Vuelve a consultar más tarde.</span>
        </div>

        <template v-else>
        <!-- Selección de Tema -->
        <div v-if="temas" class="asw-form-group">
          <label class="asw-form-label">
            <i class="ri-bookmark-line"></i>
            Tema:
          </label>
          <div class="asw-select-with-notification">
            <select v-model="siguienteTema" class="asw-select">
              <option disabled value="null">Selecciona un tema</option>
              <option v-for="tema in temas" :key="tema.id" :value="tema">{{ tema.nombre }}</option>
            </select>
            <button v-if="siguienteTema && siguienteTema.notificacion_mostrar && siguienteTema.notificacion_texto"
              @click="mostrarNotificacionTema()" class="btn-notification">
              <i class="ri-information-line"></i>
            </button>
          </div>
        </div>

        <!-- Selección de Día -->
        <div v-if="dias && dias.length > 0" class="asw-form-group">
          <label class="asw-form-label">
            <i class="ri-calendar-2-line"></i>
            Día:
          </label>
          <select v-model="dia" class="asw-select">
            <option disabled value="null">Selecciona un día</option>
            <option v-for="diaOption in dias" :key="diaOption.fecha" :value="diaOption">{{ diaOption.texto }}</option>
          </select>
        </div>
        </template>
      </div>

      <!-- Horarios Disponibles -->
      <div v-if="posibles_horas_grupos" class="horarios-section">
        <h3 class="section-title">Horarios Disponibles</h3>

        <div class="accordion-modern" role="tablist">
          <b-card no-body class="accordion-card" v-for="(item, key, number) in posibles_horas_grupos" :key="key">
            <b-card-header header-tag="header" class="accordion-header" role="tab">
              <b-button block v-b-toggle="'acordeon-' + number" class="accordion-button">
                <i class="ri-time-line"></i>
                {{ maskHourTimestamp(key) }}
                <i class="ri-arrow-down-s-line chevron"></i>
              </b-button>
            </b-card-header>
            <b-collapse :id="'acordeon-' + number" accordion="my-accordion" role="tabpanel">
              <b-card-body class="accordion-body">
                <div class="horarios-grid">
                  <div v-for="(posible_hora, index) in item" :key="index" class="horario-card">
                    <span class="horario-time">
                      <i class="ri-time-line"></i>
                      {{ showHourLocal(posible_hora) }}
                    </span>
                    <button v-if="posible_hora.disponible > 0" @click="seleccionarHora(posible_hora)"
                      :disabled="!(siguienteTema && siguienteTema.id)" class="btn-programar">
                      <i class="ri-checkbox-circle-line"></i>
                      Programar
                    </button>
                    <div v-else-if="posible_hora.mensaje_cierre" class="horario-closed">
                      <span class="closed-label">Franja cerrada</span>
                      <span class="closed-reason">{{ posible_hora.mensaje_cierre }}</span>
                    </div>
                    <button v-else @click="consultarMasOpciones(posible_hora)" class="btn-options">
                      <i class="ri-more-line"></i>
                      Más Opciones
                    </button>
                  </div>
                </div>
              </b-card-body>
            </b-collapse>
          </b-card>
        </div>
      </div>

      <!-- Día seleccionado sin horarios -->
      <div v-else-if="diaSeleccionado" class="no-availability" role="status">
        <i class="ri-information-line"></i>
        <span>No hay horarios disponibles para la fecha seleccionada. Prueba seleccionando un día diferente.</span>
      </div>

      <!-- Mensaje de confirmación -->
      <div v-if="mensaje" class="alert-success-modern" v-html="mensaje"></div>

      <!-- Link a Mis Tutorías -->
      <router-link to="/mis-tutorias" class="action-link">
        <i class="ri-file-list-line"></i>
        Ver Mis Tutorías
      </router-link>

      <!-- Sección de Instrucciones -->
      <div class="instructions-section">
        <div class="instructions-header">
          <i class="ri-information-line"></i>
          <h3>Instrucciones para programar tus tutorías</h3>
        </div>
        <p class="instructions-intro">Recuerda que puedes programar tus sesiones hasta 1 hora antes del inicio de cada
          bloque. Lee con atención las siguientes indicaciones para aprovechar al máximo el sistema de programación:
        </p>

        <div class="instructions-grid">
          <div class="instruction-card">
            <div class="instruction-header" @click="toggleCard('programacion')">
              <i class="ri-calendar-check-line instruction-icon"></i>
              <div class="instruction-title-wrapper">
                <h4>Programación de tutorías</h4>
              </div>
              <button class="btn-card-collapse" :class="{ expanded: instructionCards.programacion }">
                <i class="ri-arrow-down-s-line"></i>
              </button>
            </div>
            <transition name="collapse">
              <div v-show="instructionCards.programacion" class="instruction-content">
                <ul>
                  <li>La programación se habilita todos los días a las 6:00 a.m., abriendo los cupos del día
                    siguiente.</li>
                  <li>La asignación de tutorías se define según la orientación del Personal Trainer a través de
                    WhatsApp, quien valida las necesidades específicas del estudiante y los temas a reforzar.</li>
                  <li>El sistema te mostrará disponibilidad después de cada nueva programación realizada.</li>
                </ul>
              </div>
            </transition>
          </div>

          <div class="instruction-card">
            <div class="instruction-header" @click="toggleCard('cancelaciones')">
              <i class="ri-close-circle-line instruction-icon danger"></i>
              <div class="instruction-title-wrapper">
                <h4>Cancelaciones</h4>
              </div>
              <button class="btn-card-collapse" :class="{ expanded: instructionCards.cancelaciones }">
                <i class="ri-arrow-down-s-line"></i>
              </button>
            </div>
            <transition name="collapse">
              <div v-show="instructionCards.cancelaciones" class="instruction-content">
                <ul>
                  <li>Las tutorías se pueden cancelar con mínimo 8 horas de anticipación.</li>
                  <li>Cuando cancelas a tiempo, tu cupo se libera automáticamente para que otro estudiante pueda
                    tomarlo.</li>
                  <li>Si no cancelas y no asistes, el sistema registrará la tutoría como no asistida y se descontará
                    de tus tutorías asignadas.</li>
                </ul>
              </div>
            </transition>
          </div>

          <div class="instruction-card">
            <div class="instruction-header" @click="toggleCard('recomendaciones')">
              <i class="ri-checkbox-circle-line instruction-icon success"></i>
              <div class="instruction-title-wrapper">
                <h4>Recomendaciones</h4>
              </div>
              <button class="btn-card-collapse" :class="{ expanded: instructionCards.recomendaciones }">
                <i class="ri-arrow-down-s-line"></i>
              </button>
            </div>
            <transition name="collapse">
              <div v-show="instructionCards.recomendaciones" class="instruction-content">
                <ul>
                  <li>Las tutorías son un acompañamiento para que alcances tu meta de aprender inglés, cada tutoría es
                    un espacio para que refuerces y practiques los conocimientos vistos en las clases.</li>
                </ul>
              </div>
            </transition>
          </div>
        </div>
      </div>

    </div>


    <b-modal no-close-on-backdrop hide-header-close ok-title="Programar" @ok="reservar" @cancel="cancelarConfirmacion"
      @hide="cancelarConfirmacion" v-model="confirmando" id="modalConfirmacion" modal-class="modal-modern">
      <div v-if="agendable">
        Confirmo mi solicitud para tomar la clase {{ siguienteTema.nombre }}
        el día {{ dia.texto }} en la franja de {{ showHourLocal(horarioSeleccionado) }} en la sede
        {{ sedeActual.nombre }}.

        <br>
        <strong v-if="horarioSeleccionado.politica_cancel">{{ horarioSeleccionado.politica_cancel }}</strong>
      </div>
    </b-modal>

    <b-modal no-close-on-backdrop hide-header-close ok-only ok-title="Entiendo" @hide="mostrarSedeTutoria"
      v-model="mostrarSedeTutoria" id="modalSedeTutoria" modal-class="modal-modern">
      Esta clase solo se puede programar en sede MasterClass.
    </b-modal>

    <b-modal no-close-on-backdrop hide-header-close ok-only ok-title="Omitir" v-model="mostrandoNotificacionTema"
      id="modalNotificacionTema" :title="tituloNotificacionTema" modal-class="modal-modern">
      <p> {{ textoNotificacionTema }} </p>
      <br>
      <p class="text-center">
        <b-link v-if="enlaceNotificacionTema" :href="enlaceNotificacionTema" target="_blank">Más información</b-link>
      </p>
    </b-modal>


    <b-modal @ok="procesarAceptarTerminos" @cancel="procesarDenegarTerminos" @hide="procesarDenegarTerminos"
      no-close-on-backdrop ok-title="Acepto" cancel-title="No Acepto" v-model="mostrarTyC"
      id="modalTerminosycondiciones" title="Términos y Condiciones de Uso" modal-class="modal-modern">
      <div v-if="terminos_y_condiciones"> {{ terminos_y_condiciones }} </div>
    </b-modal>

    <b-modal @ok="procesarAceptarSegundaOpcion" @cancel="procesarCancelarSegundaOpcion"
      @hide="procesarCancelarSegundaOpcion" no-close-on-backdrop ok-title="Programar" cancel-title="No Acepto"
      v-model="mostrandoSegundaOpcion" id="modalSegundaOpcion" title="Cupos disponibles" modal-class="modal-modern">
      <div v-if="getHoraSegundaOpcion.disponible > 0">
        Encontramos cupo para que pueda programar su clase {{ segundaOpcion.tema.nombre }} el día
        {{ segundaOpcion.horarios.texto }} a las {{ getHoraSegundaOpcion.nombre_corto }}<br>
        Quiere aceptar esta opción?
      </div>
      <div v-else>
        NO ENCONTRAMOS MÁS OPCIONES
      </div>
    </b-modal>

    <b-modal hide-header hide-footer size="sm" v-model="mostrandoNoHaySegundaOpcion" id="modalNoHaySegundaOpcion"
      modal-class="modal-modern">
      NO ENCONTRAMOS MÁS OPCIONES
    </b-modal>

    <b-modal no-close-on-backdrop hide-header-close ok-title="Cambiar" cancel-title="Cancelar" @ok="procesarCambiarSede"
      @cancel="nuevaSede = null" @hide="nuevaSede = null" v-model="cambiandoTema" id="modalCambiarTema"
      modal-class="modal-sede-modern" title="Cambiar Sede">

      <div v-if="sedes.length > 0" class="modal-sede-content">
        <div class="form-group-sede">
          <label class="label-sede">Tipo de Sede:</label>
          <div class="radio-group">
            <label class="radio-option">
              <input v-model="tipoSedeSelected" type="radio" name="tipoSede" value="virtual" />
              <span>Virtual</span>
            </label>
            <label class="radio-option">
              <input v-model="tipoSedeSelected" type="radio" name="tipoSede" value="presencial" />
              <span>Presencial</span>
            </label>
          </div>
        </div>

        <div class="form-group-sede">
          <label class="label-sede">Selecciona una Sede:</label>
          <select v-model="nuevaSede" class="select-sede">
            <option disabled :value="null">Selecciona una sede</option>
            <option v-for="opcion in sedesFiltradas" :key="opcion.id" :value="opcion.id">
              {{ opcion.nombre }}
            </option>
          </select>
        </div>
      </div>
    </b-modal>

  </MainLayout>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import Vue from 'vue';
import MainLayout from '@/components/Layout/MainLayout.vue';
import PageHeader from '@/components/Layout/PageHeader.vue';

export default {
  name: 'ProgramarTutoriaPage',
  components: {
    MainLayout,
    PageHeader
  },
  data() {
    return {
      solicitud: null,
      diaSeleccionado: null,
      horarioSeleccionado: null,
      error: null,
      mensaje: null,
      confirmando: false,
      mostrandoNotificacionTema: false,
      mostrandoTyC: false,
      segundaOpcion: null,
      tituloNotificacionTema: null,
      textoNotificacionTema: null,
      enlaceNotificacionTema: '#',
      mostrandoSegundaOpcion: false,
      mostrandoNoHaySegundaOpcion: false,
      cambiandoTema: false,
      nuevaSede: null,
      tipoSedeSelected: 'presencial',
      opcion_clase: null,
      siguienteTema: null,//1:Finalizar Nivel,2:Avanzar Nivel,0:No hacer nada
      mostrarSedeTutoria: false,
      disponibilidadConsultada: false, // true cuando terminó de consultar los días disponibles
      instructionCards: {
        programacion: false,
        cancelaciones: false,
        recomendaciones: false
      }

    }
  },

  mounted() {
    this.consultarOpciones();
    this.fetchClases().then(reponse => {
      if (!this.debe_aceptar_terminos && this.clasesPendientes.length == 0) {
        this.mostrarMensajeInstrucciones();
      }
    })
    if (!this.sedes || (this.sedes && this.sedes.length == 0)) {
      this.fetchSedes()
    }
    this.actualizarDisponibilidad()
    this.verificarVariables().then((result) => {
      if (!this.tutoriasHabilitadas) {
        history.back()
      }
    })
  },
  methods: {
    ...mapActions({
      fetchTipos: 'academico/fetchTipos',
      fetchCursoActual: 'academico/fetchCursoActual',
      fetchOpciones: 'academico/fetchOpciones',
      fetchHorarios: 'academico/fetchHorarios',
      fetchDisponibilidad: 'tutoria/fetchDisponibilidad',
      agendar: 'tutoria/agendar',
      fetchClases: 'academico/fetchClases',
      aceptarTerminos: 'academico/aceptarTerminos',
      fetchSedes: 'sedes/fetchLista',
      cambiarSede: 'sedes/cambiarSede',
      fetchSedeActual: 'academico/fetchSedeActual',
      fetchCursoFinalizado: 'academico/fetchCursoFinalizado',
      verificarVariables: 'student/verificarVariables',
    }),
    ...mapMutations({
      setHorariosDisponibles: 'academico/setHorariosDisponibles',
      startProcessing: 'startProcessing',
      stopProcessing: 'stopProcessing',
      setWarning: 'setWarning',
      addMensaje: 'auth/addMensaje',
    }),
    seleccionarHora(value) {
      this.horarioSeleccionado = value
      this.confirmando = true
    },
    cancelarConfirmacion() {
      this.horarioSeleccionado = null
    },
    toggleCard(cardName) {
      this.instructionCards[cardName] = !this.instructionCards[cardName]
    },
    mostrarMensajeInstrucciones() {
      const instructionMessage = {
        variant: 'info',
        html: '<strong>📚 Información importante:</strong> El sistema mostrará la disponibilidad de horarios a partir del primer bloque programado.',
        autoDismiss: false,
      };

      setTimeout(() => {
        const messageExists = this.mensajes.some(mensaje =>
          mensaje.variant === instructionMessage.variant && mensaje.html === instructionMessage.html
        );

        if (!messageExists) {
          this.addMensaje(instructionMessage);
        }
      }, 500);
    },
    actualizarDisponibilidad() {
      this.reiniciar()
      this.disponibilidadConsultada = false
      this.fetchDisponibilidad().then((result) => {
        if (!this.tipos || (this.tipos && this.tipos.length == 0)) {
          this.fetchTipos()
        }
        if (!this.cursoActual) {
          this.fetchCursoActual()
        }
      }).catch((err) => {

      }).finally(() => {
        this.disponibilidadConsultada = true
        this.verificarSede()
      })
    },
    actualizarHorarios() {
      this.fetchHorarios()
    },
    consultarOpciones() {
      this.fetchOpciones().then(response => {
        let opcion = response.find(x => x.predeterminada);
        if (opcion) {
          this.opcion_clase = opcion.value;
        }
      });
    },
    reservar() {
      let tipo_solicitud = 'tutoria'
      let tipo_clase = 'tutoria'
      this.agendar({
        tema_id: this.siguienteTema.id,
        hora: this.horarioSeleccionado.hora,
        dia: this.diaSeleccionado.fecha,
        tipo_clase: tipo_clase,
        tipo_solicitud: tipo_solicitud
      }).then((apiResponse) => {
        if (apiResponse.error) {
          this.error = apiResponse.error
        } else {
          let texto = "Programación exitosa para " + this.showDateLocal(apiResponse.fecha + " " + apiResponse.hora) + ".<br>"
          if (apiResponse.mensaje) {
            this.mensaje = texto + ' <strong>' + apiResponse.mensaje + '</strong>'
          } else {
            this.mensaje = texto
          }

        }
      }).finally(() => {
        this.reiniciar()
        this.fetchClases()
        this.actualizarDisponibilidad()
      })
    },
    reiniciar() {
      this.diaSeleccionado = null
      this.horarioSeleccionado = null
      this.error = null
      this.setHorariosDisponibles(null)
    },
    procesarAceptarTerminos() {
      this.aceptarTerminos()
    },
    procesarDenegarTerminos() {
      if (this.debe_aceptar_terminos) {
        this.$router.push('/')
      }
    },
    consultarMasOpciones(posible_hora) {
      let payload = {
        tema_id: this.siguienteTema.id,
        fecha: this.diaSeleccionado.fecha,
        hora: posible_hora.hora,
      }
      this.startProcessing()
      Vue.http.post('ver_mas_opciones', payload).then(
        response => {
          this.segundaOpcion = response.data
          if (this.getHoraSegundaOpcion.disponible > 0) {
            this.mostrandoSegundaOpcion = true
          } else {
            this.mostrandoNoHaySegundaOpcion = true
            setTimeout(() => {
              this.mostrandoNoHaySegundaOpcion = false
              this.segundaOpcion = null
            }, 3000)
          }

        }
      ).catch(error => {

      }).finally(() => {
        this.stopProcessing()
      })
    },
    procesarAceptarSegundaOpcion() {

      this.agendar({

        tema_id: this.segundaOpcion.tema.id,
        hora: this.getHoraSegundaOpcion.hora,
        dia: this.segundaOpcion.horarios.fecha,
        tipo_clase: null,
        tipo_solicitud: 'normal',
        segunda_opcion: true
      }).then((apiResponse) => {
        if (apiResponse.error) {
          this.error = apiResponse.error
        } else {
          let texto = "Programación exitosa para " + this.showDateLocal(apiResponse.fecha + " " + apiResponse.hora) + "."
          if (apiResponse.mensaje) {
            this.mensaje = texto + ' ' + apiResponse.mensaje
          } else {
            this.mensaje = texto
          }

        }
      }).finally(() => {
        this.reiniciar()
        this.fetchClases()
        this.actualizarDisponibilidad()
      })


    },
    procesarCancelarSegundaOpcion() {
      this.mostrandoSegundaOpcion = false
      this.segundaOpcion = null
    },
    procesarCambiarSede() {
      this.cambiarSede({ sede_id: this.nuevaSede }).then((apiResponse) => {
        this.setWarning(apiResponse.mensaje)
      }).finally(() => {
        this.nuevaSede = null
        this.actualizarDisponibilidad()
        this.fetchSedeActual()
      })
    },
    mostrarNotificacionTema() {
      if (this.siguienteTema && this.siguienteTema.notificacion_mostrar && this.siguienteTema.notificacion_texto) {
        this.mostrandoNotificacionTema = true;
        this.textoNotificacionTema = this.siguienteTema.notificacion_texto
        this.enlaceNotificacionTema = this.siguienteTema.notificacion_enlace
        this.tituloNotificacionTema = this.siguienteTema.nombre;
      }
    },
    verificarSede() {
      if (this.sedeActual.id != 30) {
        this.nuevaSede = 30;
        this.procesarCambiarSede();
      }
    },
    showHourLocal(posible_hora) {
      if (this.sedeActual && this.sedeActual.es_virtual === 1 && this.userTimeZone !== 'America/Bogota') {
        let marca_tiempo = posible_hora.marca_tiempo;
        let fecha = new Date(marca_tiempo + "-05:00");
        return this.maskHour(fecha)
      } else {
        return posible_hora.nombre_corto;
      }
    },
    maskHourTimestamp(timestamp) {
      let fecha = new Date(parseInt(timestamp));
      return this.maskHour(fecha)
    },
    showDateLocal(marca_tiempo) {
      if (this.sedeActual && this.sedeActual.es_virtual === 1 && this.userTimeZone !== 'America/Bogota') {
        let fecha = new Date(marca_tiempo + "-05:00");
        return fecha.toLocaleString()
      } else {
        return marca_tiempo;
      }
    },
    maskHour(fecha) {
      return `${fecha.getHours() > 12 ? fecha.getHours() - 12 : fecha.getHours()}:${(fecha.getMinutes()).toString().padStart(2, '0')} ${fecha.getHours() >= 12 ? 'PM' : 'AM'}`;
    }

  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      cursoActual: state => state.academico.cursoActual,
      temas: state => state.tutoria.temas,
      dias: state => state.tutoria.horariosDisponibles,
      tipos: state => state.academico.tipos,
      opciones: state => state.academico.opcionesDisponibles,
      ultima_actualizacion_horarios: state => state.academico.ultima_actualizacion_horarios,
      debe_aceptar_terminos: state => state.academico.debe_aceptar_terminos,
      terminos_y_condiciones: state => state.academico.terminos_y_condiciones,
      sedes: state => state.sedes.lista,
      sedeActual: state => state.academico.sedeActual,
      variables: state => state.student.variables,
      mensajes: state => state.auth.mensajes,
    }),
    ...mapGetters({
      clasesPendientes: 'academico/clasesPendientes',
      getSede: 'sedes/getSede',
    }),
    tutoriasHabilitadas() {
      return this.variables && this.variables.habilitar_tutoria == 1 ? true : false;
    },
    // Una vez consultada la disponibilidad: sin días o sin temas no hay nada que programar
    sinHorariosHabilitados() {
      if (!this.disponibilidadConsultada) return false;
      const sinDias = !this.dias || this.dias.length === 0;
      const sinTemas = !this.temas || this.temas.length === 0;
      return sinDias || sinTemas;
    },

    mostrarTyC: {
      get() {
        return this.debe_aceptar_terminos || this.mostrandoTyC
      },
      set(value) {
        this.mostrandoTyC = value
      }
    },
    dia: {
      get() {
        return this.diaSeleccionado;
      },
      set(value) {
        this.diaSeleccionado = value;
      }
    },
    hora: {
      get() {
        return this.horarioSeleccionado;
      },
      set(value) {
        this.horarioSeleccionado = value;
      }
    },
    puedeCambiarSede() {
      return false;
    },
    esClase0() {
      if (this.siguienteTema) {
        if (this.siguienteTema.tipo === 'clase_0') {
          return true;
        }
      }
      return false;
    },
    posibles_horas: {
      get() {
        if (this.diaSeleccionado) {
          return this.diaSeleccionado.horas;
        } else {
          return null
        }
      },
      set(value) {

      }
    },
    posibles_horas_grupos: {
      get() {
        // Un objeto vacío ({}) es truthy: se devuelve null si el día no tiene ningún grupo de horas
        const grupos = this.diaSeleccionado && this.diaSeleccionado.grupos_horas;
        if (grupos && Object.keys(grupos).length > 0) {
          return grupos;
        }
        return null
      },
      set(value) {

      }
    },
    agendable: {
      get() {
        return (this.siguienteTema && this.dias && this.diaSeleccionado && this.horarioSeleccionado)
      },
      set(value) { }
    },
    lastTime() {
      if (this.ultima_actualizacion_horarios) {
        return this.ultima_actualizacion_horarios.toLocaleString() // 'es-CO', {timeZone:"America/Bogota"}
      }
      return null
    },
    zonaRegional() {
      const zonaVisible = this.userTimeZone === 'America/Bogota'
        ? 'América/Bogotá'
        : this.userTimeZone;
      return `(${zonaVisible})`;
    },
    getHoraSegundaOpcion() {
      if (this.segundaOpcion && this.segundaOpcion.horarios && this.segundaOpcion.horarios.horas && this.segundaOpcion.horarios.horas.length > 0) {
        return this.segundaOpcion.horarios.horas[0]
      }
      return false
    },
    sedesFiltradas() {
      return this.sedes.filter(element => {
        if (this.tipoSedeSelected == 'presencial') {
          return element.es_virtual == 0
        } else {
          return element.es_virtual == 1
        }

      })
    },

    minDate() {
      if (this.opciones) {
        let filtro = this.opciones.find(x => x.value == 'tutoria')
        if (filtro) {
          return this.maskHour(new Date(filtro.min_date + "-05:00"))
        }
      }
      return "";
    },
    maxDate() {
      if (this.opciones) {
        let filtro = this.opciones.find(x => x.value == 'tutoria')
        if (filtro) {
          return this.maskHour(new Date(filtro.max_date + "-05:00"))
        }
      }
      return "";
    },
    headerSubtitle() {
      if (this.lastTime) {
        return `Última actualización: ${this.lastTime} ${this.zonaRegional}`;
      }
      return 'Programa tu sesión de refuerzo';
    },
    userTimeZone() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    },

  }
}
</script>

<style src="../../assets/styles/shared-forms.css"></style>
<style src="../../assets/styles/shared-scheduling.css"></style>

<style scoped>
/* ============================================================================
   ESTILOS ESPECÍFICOS DE TUTORÍA
   ============================================================================ */

/* Header con más padding */
.header-content {
  padding: 0 1.5rem;
  gap: 1.5rem;
}

/* Contenedor con padding diferente */
.scheduling-container {
  padding: 0 1.5rem 3rem;
}

/* Mensaje cuando no hay días disponibles */
.no-availability {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: rgba(13, 78, 140, 0.1);
  border-left: 3px solid var(--app-primary-blue);
  border-radius: 8px;
  color: var(--theme-text-secondary);
  font-size: 0.9rem;
  line-height: 1.6;
}

.no-availability i {
  flex-shrink: 0;
  color: var(--app-primary-blue);
}

html.dark-theme .no-availability i {
  color: var(--app-color-cyan-light);
}

/* Acordeón específico de tutoría */
.accordion-modern {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.accordion-card {
  background: var(--theme-bg-card);
  border: 1px solid var(--theme-border-color);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--theme-shadow-sm);
}

.accordion-header {
  padding: 0;
  background: var(--theme-bg-secondary);
  border-bottom: 1px solid var(--theme-border-color);
}

.accordion-button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: var(--theme-bg-secondary) !important;
  border: none !important;
  color: var(--theme-text-primary) !important;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: none !important;
}

.accordion-button:hover {
  background: rgba(13, 78, 140, 0.1) !important;
  color: var(--theme-text-primary) !important;
}

.accordion-button:not(.collapsed),
.accordion-button:active,
.accordion-button.active {
  background: rgba(13, 78, 140, 0.15) !important;
  color: var(--app-primary-blue) !important;
  border-color: transparent !important;
}

.accordion-button:focus,
.accordion-button:focus-visible {
  outline: none !important;
  box-shadow: 0 0 0 3px rgba(13, 78, 140, 0.2) !important;
}

.accordion-button svg:first-child {
  color: var(--app-primary-blue);
  flex-shrink: 0;
}

.accordion-button .chevron {
  color: var(--theme-text-secondary);
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.accordion-button.not-collapsed .chevron {
  transform: rotate(180deg);
}

.accordion-body {
  padding: 1rem;
}

/* Select con notification específico de tutoría */
.asw-select-with-notification {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.asw-select {
  flex: 1;
}

/* Wrapper para el título de instrucción en tutoría */
.instruction-title-wrapper {
  flex: 1;
}

.instruction-title-wrapper h4 {
  font-size: 1rem;
  font-weight: 700;
  color: var(--theme-text-primary);
  margin: 0;
}

/* Responsive específico de tutoría */
@media (max-width: 768px) {
  .header-content {
    padding: 0 1rem;
  }

  .scheduling-container {
    padding: 0 1rem 2rem;
  }

  .asw-select-with-notification {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-notification {
    width: 100%;
  }

  .horario-closed {
    text-align: left;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .instruction-title-wrapper h4 {
    font-size: 0.9rem;
  }
}
</style>

<style src="../../assets/styles/shared-modals.css"></style>
